import { useState, useEffect } from "react"
import { Eye, EyeOff, Loader2, Lock, Shield, KeyRound } from "lucide-react"
import apiClient from "../api"

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [validationState, setValidationState] = useState({
    username: 'idle',
    password: 'idle'
  })

  // Reduce animations on low-end devices
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    // Check if user prefers reduced motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mediaQuery.matches)

    const handleChange = (e) => setReduceMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  async function handleLogin(e) {
    e.preventDefault()
    setError("")
    setIsLoading(true)
    setIsSubmitted(true)

    // Only show validation animation if motion is not reduced
    if (!reduceMotion) {
      setValidationState({ username: 'validating', password: 'validating' })
      await new Promise(resolve => setTimeout(resolve, 600))
    }

    try {
      await apiClient.login(username, password)
      onLogin()
    } catch (err) {
      setError(err.response?.data?.message || "Invalid username or password")
      setValidationState({
        username: 'invalid',
        password: 'invalid'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid = username.trim() && password.trim()

  // Optimized validation styles
  const getValidationStyle = (field) => {
    const state = validationState[field]

    if (reduceMotion) {
      // Simplified styles for reduced motion
      switch (state) {
        case 'valid':
          return { borderColor: '#10b981' }
        case 'invalid':
          return { borderColor: '#ef4444' }
        default:
          return { borderColor: '#374151' }
      }
    }

    switch (state) {
      case 'validating':
        return {
          borderColor: '#8b5cf6',
          boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.1)'
        }
      case 'valid':
        return {
          borderColor: '#10b981',
          boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)'
        }
      case 'invalid':
        return {
          borderColor: '#ef4444',
          boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.1)'
        }
      default:
        return {
          borderColor: '#374151'
        }
    }
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 md:p-6 relative overflow-hidden">
      {/* Simplified background - reduced particle count */}
      <div className="fixed inset-0 bg-black">
        {/* Subtle grid pattern - less intensive */}
        {!reduceMotion && (
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `linear-gradient(90deg, transparent 49%, rgba(139, 92, 246, 0.1) 50%, transparent 51%)`,
              backgroundSize: '40px 40px'
            }}
          />
        )}

        {/* Reduced number of particles */}
        {!reduceMotion && [...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute h-1 w-1 bg-indigo-500/20 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: reduceMotion ? 'none' : `pulse ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}

        {/* Single gradient orb - reduced blur */}
        {!reduceMotion && (
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-900/5 rounded-full blur-xl" />
        )}
      </div>

      <div className="max-w-md w-full space-y-6 md:space-y-8 relative z-10">
        {/* Optimized header */}
        <div className="text-center space-y-4">
          <div className="relative inline-block">
            {/* Conditional orbital rings */}
            {!reduceMotion && (
              <>
                <div className="absolute inset-0 animate-spin-slow">
                  <div className="h-20 w-20 md:h-24 md:w-24 rounded-full border border-transparent border-t-indigo-500/20 border-r-purple-500/10"></div>
                </div>
                <div className="absolute inset-0 animate-spin-slow-reverse opacity-30">
                  <div className="h-24 w-24 md:h-28 md:w-28 rounded-full border border-transparent border-b-purple-500/10 border-l-indigo-500/20"></div>
                </div>
              </>
            )}

            {/* Main logo - simplified */}
            <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-gray-900 border border-gray-800 flex items-center justify-center mx-auto relative">
              <Lock className="h-8 w-8 md:h-10 md:w-10 text-indigo-400" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Welcome Back
              </span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base">
              Sign in to access your secure dashboard
            </p>
          </div>
        </div>

        {/* Login card - simplified */}
        <div className="bg-gray-900/80 backdrop-blur-sm py-6 md:py-8 px-4 md:px-8 rounded-xl border border-gray-800 relative">
          <form className="space-y-4 md:space-y-5" onSubmit={handleLogin}>
            {/* Username field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                  Username
                </label>
                {!reduceMotion && validationState.username === 'validating' && (
                  <div className="flex items-center gap-1 text-xs text-purple-400">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full animate-pulse" />
                    <span>Validating</span>
                  </div>
                )}
              </div>

              <div className="relative">
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value)
                    setError("")
                    setValidationState(prev => ({ ...prev, username: 'idle' }))
                  }}
                  onBlur={() => {
                    if (username.trim() && !reduceMotion) {
                      setValidationState(prev => ({ ...prev, username: 'validating' }))
                      setTimeout(() => {
                        setValidationState(prev => ({ ...prev, username: 'valid' }))
                      }, 400)
                    }
                  }}
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800/50 border text-gray-100 placeholder-gray-500 transition-all duration-200 focus:outline-none focus:border-indigo-500"
                  style={getValidationStyle('username')}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  autoComplete="username"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <KeyRound className="h-5 w-5 text-gray-500" />
                </div>
              </div>

              {isSubmitted && !username.trim() && (
                <div className="flex items-center gap-1 mt-1 text-sm text-red-400">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  Username is required
                </div>
              )}
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <>
                      <EyeOff size={14} />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <Eye size={14} />
                      <span>Show</span>
                    </>
                  )}
                </button>
              </div>

              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setError("")
                    setValidationState(prev => ({ ...prev, password: 'idle' }))
                  }}
                  onBlur={() => {
                    if (password.trim() && !reduceMotion) {
                      setValidationState(prev => ({ ...prev, password: 'validating' }))
                      setTimeout(() => {
                        setValidationState(prev => ({ ...prev, password: 'valid' }))
                      }, 400)
                    }
                  }}
                  className="w-full pl-10 pr-12 py-3 rounded-lg bg-gray-800/50 border text-gray-100 placeholder-gray-500 transition-all duration-200 focus:outline-none focus:border-indigo-500"
                  style={getValidationStyle('password')}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <Shield className="h-5 w-5 text-gray-500" />
                </div>

                {/* Eye icon inside the input */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {isSubmitted && !password.trim() && (
                <div className="flex items-center gap-1 mt-1 text-sm text-red-400">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  Password is required
                </div>
              )}
            </div>
            {/* Error message */}
            {error && (
              <div className="bg-red-900/30 border border-red-800/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="h-4 w-4 rounded-full bg-red-500" />
                  </div>
                  <div>
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={!isFormValid || isLoading}
                className={`w-full flex justify-center items-center py-3 md:py-3.5 px-4 border rounded-lg text-sm font-medium text-white transition-all duration-200 relative ${!isFormValid || isLoading
                  ? "bg-indigo-900/50 border-indigo-800/50 cursor-not-allowed"
                  : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 border-indigo-500/20"
                  }`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className={`h-5 w-5 ${reduceMotion ? '' : 'animate-spin'}`} />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>

        </div>

        {/* Copyright */}
        <div className="text-center">
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} Zenkai. All rights reserved.
          </p>
          <p className="mt-1 text-xs text-gray-700">
            Crated by Cyclone Addons And Aurazod
          </p>
        </div>
      </div>

      {/* Conditional styles only if not reduced motion */}
      {!reduceMotion && (
        <style>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes spin-slow-reverse {
            from { transform: rotate(360deg); }
            to { transform: rotate(0deg); }
          }
          
          .animate-spin-slow {
            animation: spin-slow 20s linear infinite;
          }
          
          .animate-spin-slow-reverse {
            animation: spin-slow-reverse 15s linear infinite;
          }
          
          /* Optimize animations for performance */
          @media (prefers-reduced-motion: reduce) {
            .animate-spin-slow,
            .animate-spin-slow-reverse,
            .animate-pulse {
              animation: none;
            }
          }
        `}</style>
      )}
    </div>
  )
}